import type { MouseEventHandler } from 'react';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import * as S from './PaintColour.style';
import { motion } from 'framer-motion';
import { CloseLine, IconPlus, ResenePickColour } from '@archipro-design/icons';
import { Colour } from '@modules/paint-picker-new/component/paint-colours/Colour';
import type { PaintProduct } from '@modules/paint-picker-new/type';
import { usePaintPickerContext } from '@modules/paint-picker-new/context/PaintPickerContext';

interface PaintColourProps extends PaintProduct {
    onMouseEnter?: MouseEventHandler<HTMLDivElement>;
}

export const PaintColour = ({
    title,
    rgb,
    isWoodstain,
    onMouseEnter,
}: PaintColourProps) => {
    const { desktop } = useAppDisplayModeContext();
    const { css } = useStyles();

    return (
        <div className={css(S.ColourRow)} onMouseEnter={onMouseEnter}>
            <div className={css(S.ColourDetails)}>
                <Colour rgb={rgb} isWoodstain={isWoodstain} />
                <motion.div
                    variants={desktop ? S.titleAnimation : undefined}
                    className={`${css(S.ColourName)} paint-colour-name`}
                >
                    {trimColorPrefix(title)}
                </motion.div>
            </div>
        </div>
    );
};

export const PaintColourPicker = () => {
    const { desktop } = useAppDisplayModeContext();
    const { css } = useStyles();

    const {
        state: { isPicking, selectedColorIndex, previewPaint },
        setPickingProcess,
        addPaintToSelection,
        setSelectedColour,
    } = usePaintPickerContext();

    const isReplacingColour = isPicking && selectedColorIndex !== null;
    const defaultColourRGB = { r: 255, g: 255, b: 255 };

    const isDarkBackground = () => {
        if (previewPaint) {
            const { r, g, b } = previewPaint.rgb;

            // This formula calculates the perceived brightness of a color based on its RGB (red, green, blue) components.
            // The coefficients (299, 587, and 114) are used to weight the contribution of each color channel according to human perception,
            // as the human eye is more sensitive to green light than red or blue.
            // This formula is commonly used in digital imaging to determine how bright a color appears to humans.
            return (r * 299 + g * 587 + b * 114) / 1000 < 128;
        }
    };

    return (
        <div
            className={css(S.ColourRow, S.ColourPickRow)}
            onClick={() => {
                if (!isReplacingColour) {
                    setSelectedColour(null);
                    setPickingProcess(!isPicking);
                }
            }}
        >
            <div className={css(S.ColourDetails)}>
                {desktop ? (
                    <>
                        <div className={css(S.ColourPick)}>
                            <Colour rgb={defaultColourRGB} />
                            <motion.div
                                variants={S.pickIconAnimation}
                                className={css(S.ColourPickIcon)}
                            >
                                {!isReplacingColour && isPicking ? (
                                    <CloseLine />
                                ) : (
                                    <ResenePickColour />
                                )}
                            </motion.div>

                            {desktop && (
                                <motion.div
                                    variants={S.pickCollapsedIconAnimation}
                                    className={css(S.ColourPickCollapsedIcon)}
                                >
                                    <IconPlus />
                                </motion.div>
                            )}
                        </div>

                        <motion.p
                            variants={S.titleAnimation}
                            className={css(S.ColourName)}
                        >
                            {!isReplacingColour && isPicking
                                ? 'Close Picker'
                                : 'Add Colour'}
                        </motion.p>
                    </>
                ) : (
                    <>
                        <div
                            className={css(S.ColourPick)}
                            onTouchStart={() =>
                                previewPaint && addPaintToSelection()
                            }
                        >
                            <Colour
                                rgb={previewPaint?.rgb ?? defaultColourRGB}
                            />
                            <div
                                className={css(
                                    S.ColourPickIcon,
                                    S.ColourPickIconMobile
                                )}
                            >
                                {previewPaint ? (
                                    <IconPlus
                                        color={
                                            isDarkBackground() ? '#fff' : '#000'
                                        }
                                    />
                                ) : (
                                    <ResenePickColour />
                                )}
                            </div>
                        </div>

                        <div className={css(S.ColourName)}>
                            {previewPaint
                                ? trimColorPrefix(previewPaint.title)
                                : 'Tap a Colour'}
                        </div>

                        <div
                            className={`${css(S.AddToPaletteMobile)} ${!previewPaint && css(S.AddToPaletteMobileDisabled)}`}
                            onTouchStart={() =>
                                previewPaint && addPaintToSelection()
                            }
                        >
                            Add to Palette
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const trimColorPrefix = (title: string) => title.replace(/^Resene /i, '');
